import React, { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import type { EmployeeDesignation, UserRole } from '../../types';
import { useCRM } from '../../utils/CRMContext';
import { hasReleaseAudienceSelection, listReleaseNotifyEmployees } from '../../utils/releaseAudience';

export const RELEASE_AUDIENCE_OPTIONS: { role: UserRole; label: string }[] = [
  { role: 'admin', label: 'Admins' },
  { role: 'employee', label: 'Employees' },
  { role: 'staff', label: 'Field staff' },
  { role: 'designer', label: 'Designers' },
  { role: 'architect', label: 'Architects' },
  { role: 'engineer', label: 'Engineers' },
  { role: 'client', label: 'Clients' },
  { role: 'supplier', label: 'Suppliers' },
];

export const RELEASE_DESIGNATION_OPTIONS: { designation: EmployeeDesignation; label: string }[] = [
  { designation: 'executive_assistant', label: 'EAs' },
];

interface ReleaseAudiencePickerProps {
  selected: UserRole[];
  selectedDesignations?: EmployeeDesignation[];
  selectedUserIds?: string[];
  onChange: (roles: UserRole[]) => void;
  onDesignationsChange?: (designations: EmployeeDesignation[]) => void;
  onUserIdsChange?: (userIds: string[]) => void;
  disabled?: boolean;
  /** Heading above the role chips. */
  label?: string;
  /** Also list QA/test accounts in "Specific people" (pilot enrolment). */
  includeTestUsers?: boolean;
  /** Also list designers / architects / engineers in "Specific people". */
  includeDesignPartners?: boolean;
}

const ReleaseAudiencePicker: React.FC<ReleaseAudiencePickerProps> = ({
  selected,
  selectedDesignations = [],
  selectedUserIds = [],
  onChange,
  onDesignationsChange,
  onUserIdsChange,
  disabled,
  label = 'Who should see this update?',
  includeTestUsers = false,
  includeDesignPartners = false,
}) => {
  const { crmUsers } = useCRM();
  const [peopleQuery, setPeopleQuery] = useState('');
  const employees = useMemo(
    () => listReleaseNotifyEmployees(crmUsers, { includeTestUsers, includeDesignPartners }),
    [crmUsers, includeTestUsers, includeDesignPartners],
  );
  const q = peopleQuery.trim().toLowerCase();
  const visibleEmployees = useMemo(() => {
    if (!q) return employees;
    return employees.filter((u) => {
      const name = (u.displayName || '').toLowerCase();
      const email = (u.email || '').toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [employees, q]);

  const hasAudience = (roles: UserRole[], designations: EmployeeDesignation[], userIds: string[]) =>
    hasReleaseAudienceSelection(roles, designations, userIds);

  const toggleRole = (role: UserRole) => {
    if (disabled) return;
    if (selected.includes(role)) {
      const remainingRoles = selected.filter((r) => r !== role);
      if (!hasAudience(remainingRoles, selectedDesignations, selectedUserIds)) return;
      onChange(remainingRoles);
    } else {
      onChange([...selected, role]);
    }
  };

  const toggleDesignation = (designation: EmployeeDesignation) => {
    if (disabled || !onDesignationsChange) return;
    if (selectedDesignations.includes(designation)) {
      const remaining = selectedDesignations.filter((d) => d !== designation);
      if (!hasAudience(selected, remaining, selectedUserIds)) return;
      onDesignationsChange(remaining);
    } else {
      onDesignationsChange([...selectedDesignations, designation]);
    }
  };

  const toggleUser = (uid: string) => {
    if (disabled || !onUserIdsChange) return;
    if (selectedUserIds.includes(uid)) {
      const remaining = selectedUserIds.filter((id) => id !== uid);
      if (!hasAudience(selected, selectedDesignations, remaining)) return;
      onUserIdsChange(remaining);
    } else {
      onUserIdsChange([...selectedUserIds, uid]);
    }
  };

  return (
    <div className="release-audience-picker">
      <p className="release-audience-label">{label}</p>
      <div className="release-audience-options">
        {RELEASE_AUDIENCE_OPTIONS.map(({ role, label }) => {
          const active = selected.includes(role);
          return (
            <button
              key={role}
              type="button"
              className={`release-audience-chip ${active ? 'release-audience-chip--active' : ''}`}
              onClick={() => toggleRole(role)}
              disabled={disabled}
              aria-pressed={active}
            >
              {label}
            </button>
          );
        })}
        {onDesignationsChange &&
          RELEASE_DESIGNATION_OPTIONS.map(({ designation, label }) => {
            const active = selectedDesignations.includes(designation);
            return (
              <button
                key={designation}
                type="button"
                className={`release-audience-chip ${active ? 'release-audience-chip--active' : ''}`}
                onClick={() => toggleDesignation(designation)}
                disabled={disabled}
                aria-pressed={active}
              >
                {label}
              </button>
            );
          })}
      </div>

      {onUserIdsChange && (
        <div className="release-audience-people">
          <p className="release-audience-label">Specific people</p>
          <label className="release-audience-search">
            <Search size={14} aria-hidden />
            <input
              type="search"
              value={peopleQuery}
              onChange={(e) => setPeopleQuery(e.target.value)}
              placeholder="Search employees…"
              disabled={disabled}
              aria-label="Search employees to notify"
            />
          </label>
          <div className="release-audience-options">
            {visibleEmployees.map((u) => {
              const active = selectedUserIds.includes(u.uid);
              return (
                <button
                  key={u.uid}
                  type="button"
                  className={`release-audience-chip ${active ? 'release-audience-chip--active' : ''}`}
                  onClick={() => toggleUser(u.uid)}
                  disabled={disabled}
                  aria-pressed={active}
                  title={u.email || u.displayName}
                >
                  {u.displayName || u.email || u.uid}
                </button>
              );
            })}
            {employees.length === 0 && (
              <span className="release-audience-people-empty">No employees loaded yet</span>
            )}
            {employees.length > 0 && visibleEmployees.length === 0 && (
              <span className="release-audience-people-empty">No matching employees</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReleaseAudiencePicker;
